import React, {
	useMemo, useRef, useState, useEffect,
} from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import * as ChatActions from '../../stores/actions/chat';
import {
	ChatReducerState, ChatPost, Document, ChatRoom,
} from '../../stores/reducers/chat';
import { SessionState, TABS } from '../../stores/reducers/session';
import Socket, { ConnectedUser } from '../../connection/socket';
import ChatLink from './chat-link';
import Avatar from '../general-components/avatar';
import { UploadDocument } from '../../connection/upload-document';
import ChatMessage from './chat-message';
import * as Utils from '../../utils/utils';
import LoadingSpinner from '../general-components/loading-spinner';
import colors from '../../utils/colors';
import { InvitationsState } from '../../stores/reducers/invitations';
import { SettingsReducerState } from '../../stores/reducers/settings';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import { random } from '../../utils/random';
import Thumbnail from './chat-document-thumbnail';
import * as NotificationBuilder from '../../utils/notifications-builder';
import usePrevious from '../../utils/use-previous';
import useScrolledToBottom from '../../hooks/use-scrolled-to-bottom';
import useChat from '../../hooks/use-chat';
import * as Storage from '../../utils/local-storage';
import * as ChatApi from '../../connection/chat';
import { SUPPORTED_CHAT_FILE_FORMATS } from '../../types/constants';

const isProduction = window.location.hostname.includes('brandlive.com');

interface OwnProps {
	// props that will be passed to this component
	socket: Socket | null;
}

interface StateProps {
	// the reducer you want to import into this component
	SessionReducer: SessionState;
	ChatReducer: ChatReducerState;
	InvitationsReducer: InvitationsState;
	SettingsReducer: SettingsReducerState;
}

interface DispatchProps {
	// the actions you want to fire from this component
	newMessage: Function;
	back: Function;
	setSeen: Function;
}

const mapStateToProps = (state: any, ownProps: any) => ({ ...state, ...ownProps });

const mapDispatchToProps = (dispatch: Function) => ({
	// the actions you want to fire from this component
	newMessage: (message: ChatPost) => {
		dispatch(ChatActions.newMessage(message));
	},
	back: () => {
		dispatch(ChatActions.setSelectedRoom(null));
	},
	setSeen: (token: string, meeting: number, room: number, messages: number[]) => {
		dispatch(
			ChatActions.setRoomMessagesSeen(
				token,
				meeting,
				room,
				messages,
			),
		);
	},
});

const getDocIcon = (doc: Document): string => {
	const fileExtention = doc.location.split('.').pop();
	switch (fileExtention) {
		case 'pdf':
			return 'icon-file-pdf';
		case 'xls': case 'xlsx':
			return 'icon-file-excel';
		case 'ppt': case 'pptx':
			return 'icon-file-powerpoint';
		case 'doc': case 'docx':
			return 'icon-file-word';
		case 'jpg': case 'jpeg': case 'png': case 'gif':
			return 'icon-image';
		case 'zip': case 'sitx': case '7z': case 'rar': case 'gz':
			return 'icon-file-zip';
		default:
			return 'icon-file-plain';
	}
};

function extractUserFromInvite(user: any): any {
	return user.profile || user.gr_user;
}

export function chatInputChange(
	e: React.FormEvent<HTMLSpanElement>,
	callback: (innerText: string) => void,
) {
	const { innerText } = e.target as HTMLSpanElement;

	callback(innerText);
}

export function chatKeyInput(
	e: React.KeyboardEvent<HTMLSpanElement>,
	callback: () => void,
) {
	const { key, shiftKey } = e;

	if (key === 'Enter') {
		if (!shiftKey) {
			callback();
		}
	}
}

type Props = StateProps & DispatchProps & OwnProps;
function ChatRoomComponent(props: Props) {
	const {
		socket,
		back,
		setSeen,
		SessionReducer: {
			user,
			token,
			meeting,
			connectedUsers,
			activeTab,
		},
		ChatReducer: {
			selectedRoom,
			rooms,
		},
		InvitationsReducer: {
			invitations,
		},
	} = props;

	const { setChatNotificationsSeen } = useChat();

	const currentRoom = useMemo(() => rooms
		.find((room: ChatRoom) => room.id === selectedRoom), [selectedRoom, rooms, rooms.length]);

	const usersFromMessages = useMemo(() => currentRoom?.messages
		.map((mess: ChatPost) => mess.sender) ?? [], [currentRoom]);

	const possibleUsers = useMemo(() => Utils.uniqueArray('gr_user', [
		...invitations.map(extractUserFromInvite).filter((profile: any) => !!profile.gr_user),
		...usersFromMessages,
		...connectedUsers,
	]), [connectedUsers, usersFromMessages, invitations]);

	const participants = useMemo(() => (currentRoom?.id === 0
		? possibleUsers
		: possibleUsers.filter((connUser: ConnectedUser) => currentRoom?.participants
			.includes(connUser.gr_user))), [currentRoom, possibleUsers]);

	const [inputText, setInputText] = useState('');
	const [attachment, setAttachment] = useState<any>(null);
	const [uploading, setUploading] = useState<boolean>(false);
	const [mentionedUsers, setMentionedUsers] = useState<ConnectedUser[]>([]);
	const [composing, setComposing] = useState<boolean>(false);
	const file = useRef<HTMLInputElement | null>(null);
	const form = useRef<HTMLFormElement | null>(null);
	const input = useRef<HTMLSpanElement | null>(null);
	const container = useRef<HTMLDivElement | null>(null);

	const prevRoomMessages = usePrevious(currentRoom?.messages);
	const prevActiveTab = usePrevious(activeTab);
	const { scrolledToBottom } = useScrolledToBottom({ scrolledContainerRef: container?.current });

	async function sendMessage() {
		if (
			(!inputText && !attachment)
			|| !token
			|| !user
			|| !meeting
			|| !currentRoom
			|| composing
		) {
			return;
		}

		const message = {
			id: 0,
			sender: user,
			meeting: meeting.meeting,
			room: currentRoom.id,
			created: Date.now(),
			message: inputText,
			seen: false,
			attachment: attachment || undefined,
			randomId: random(20),
		};

		message.message = message.message.replace(/\n/g, '\n\n');

		// Do not send blank messages
		if (!message.message.trim()) return;

		// send chat to API, make sure it's successful before sending it to other user in room
		try {
			const res = await ChatApi.PostChat(
				token,
				meeting.meeting,
				inputText,
				isProduction ? attachment?.document : attachment?.document_staging,
				currentRoom?.id,
				undefined,
			);
			if (!res) {
				console.error('unable to create direct chat message');
				return;
			}
			message.id = res.insertId;
		} catch (e) {
			console.error(`unable to create chat room message due to ${(e as Error).message}`);
			return;
		}

		// only send the socket message if the user's socket message is correct
		if (socket) {
			socket.sendRoomChat(`room.${meeting.meeting}.${currentRoom.id}`, message);
		}

		if (mentionedUsers.length > 0) {
			NotificationBuilder.commentMentionNotification(token, user, mentionedUsers, meeting);
		} else {
			NotificationBuilder.commentBasicNotification(token, user, participants, meeting);
		}

		// clear input
		setInputText('');
		setMentionedUsers([]);
		setAttachment(null);
		if (input.current) {
			input.current.innerText = '';
		}
	}

	function submitForm(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		sendMessage();
	}

	function goBack() {
		back();
	}

	function selectAttachment() {
		file.current?.click();
	}

	async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
		if (e.target.files) {
			try {
				setUploading(true);
				const result = await UploadDocument(token, e.target.files);
				setAttachment(result);
				setUploading(false);
			} catch (error) {
				setUploading(false);
				console.warn(error);
			}
		}
		if (file.current) { file.current.value = ''; }
	}

	function clearAttachment() {
		setAttachment(null);
		if (file.current) { file.current.value = ''; }
	}

	useEffect(() => {
		const roomNumber = currentRoom?.id;
		const messages: number[] = currentRoom?.messages.reduce((acc: number[], cur?: ChatPost) => {
			if (cur?.id && (!cur.seen || cur.isNew)) {
				return [...acc, cur.id];
			}

			return acc;
		}, []) ?? [];

		if (
			token
			&& meeting?.meeting
			&& roomNumber !== undefined
			&& messages?.length
			&& activeTab === 'chat'
		) {
			setSeen(token, meeting?.meeting, roomNumber, messages);
		}

		const highestMessageId: number | undefined = currentRoom?.messages.length
			? currentRoom?.messages.map((message) => message.id).sort((a, b) => b - a)[0]
			: undefined;

		if (highestMessageId && meeting?.meeting && roomNumber !== undefined && user?.gr_user) {
			const currentChatRoomsStatus = Storage.getStorageObject(
				`chat-rooms-last-seen-${meeting?.meeting}-${user.gr_user}`,
			);

			Storage.setStorageObject(`chat-rooms-last-seen-${meeting?.meeting}-${user.gr_user}`, {
				...currentChatRoomsStatus,
				[`${roomNumber}`]: highestMessageId,
			});
		}
	}, [currentRoom, currentRoom?.messages, activeTab, meeting, setSeen, token]);

	const scrollToBottom = () => {
		// scroll to bottom of the chat window if the user:
		// - previously scrolled to the bottom and a new message is sent or received
		// - entered a new chat room
		// - changed tabs
		if (container.current &&
			((prevRoomMessages?.length !== currentRoom?.messages.length && scrolledToBottom) ||
				!prevRoomMessages || prevActiveTab !== TABS.CHAT)) {
			container.current.scrollTo({ top: container.current.scrollHeight, behavior: 'auto' });
		}
	};

	// resizeObserver is used to observe the final size of the container after it finishes
	// animating in, so we can then scroll to the bottom (if necessary).
	const resizeObserver = new ResizeObserver(() => { scrollToBottom() });

	useEffect(() => {
		if (container.current) {
			resizeObserver.observe(container.current);

			setTimeout(() => {
				// we are only observing container resizes in order to scroll to the bottom
				// after it animates in, so disconnect the observer shortly after.
				if (container.current) { resizeObserver.disconnect(); }
			}, 500);
		}
		return () => { if (container.current) { resizeObserver.disconnect(); } };
	}, []);

	useEffect(() => {
		scrollToBottom();
		setChatNotificationsSeen();
	}, [currentRoom?.messages, activeTab]);

	function getBadge(room: ChatRoom): string {
		if (room.name === 'Presenter chat') {
			const possUsers = [...possibleUsers];
			const sendersFromMessages = room?.messages.map((mess: ChatPost) => mess.sender) ?? [];

			const allParticipants = Utils.uniqueArray('email', [
				...sendersFromMessages,
				...possUsers,
			]);

			return `${allParticipants.length} ${(allParticipants.length) !== 1 ? 'Members' : 'Member'}`;
		}
		const sendersFromMessages = room?.messages.map((mess: ChatPost) => mess.sender?.gr_user) ?? [];
		const totalLength = new Set([...room.participants, ...sendersFromMessages]).size;

		return `${totalLength} ${(totalLength) !== 1 ? 'Members' : 'Member'}`;
	}

	function getName(name: string): string {
		const fileNameSplit = name.split('.');
		fileNameSplit.pop();
		const fileName = fileNameSplit.join('');
		return `${fileName}.`;
	}

	return (
		<div className="room-wrapper display-flex flex-flow-column height-full">
			<div>
				<button className="chat-back-button" onClick={goBack}>
					<Icon name={ICONS.ARROW_LEFT} color={COLORS.WHITE} size={17}></Icon>
					<span>All</span>
				</button>
			</div>

			<div className='chat-top height-fit-content max-height-half padding-bottom-medium'>
				{!!currentRoom && (
					<ChatLink
						room={currentRoom}
						icon={<Icon name={ICONS.PEOPLE} size={22} color={COLORS.BLACK} ></Icon>}
						badge={getBadge(currentRoom)}
						title={currentRoom.name}
						message={currentRoom.messages?.[0]}
						showParticipants={true}
						inRoom={true}
						socket={socket}
					>
						<div className="room-participants">
							{participants
								// eslint-disable-next-line max-len
								.map((participant: ConnectedUser, index: number) => (participant.gr_user !== user!.gr_user
									? (
										<div key={index} className="button invite-badge background-dark margin-right-small margin-top-small">
											<Avatar user={participant} size={'xsmall'} noBorder={true} />
											<span className='margin-right-small'>{Utils.getNameOrInitials(participant, true)}</span>
										</div>
									)
									: null))}
						</div>
					</ChatLink>
				)}
			</div>

			<div className='chat-container height-full overflow-scroll-y' ref={container}>
				<div className="messages-container">
					{currentRoom?.messages.map((message: ChatPost, index: number) => (
						<ChatMessage
							key={message.id ? message.id : index + (message.randomId || '')}
							post={message}
							user={message.sender}
							isChild={
								currentRoom.messages[index + 1]?.sender
								&& currentRoom.messages[index + 1]?.sender?.gr_user === message?.sender?.gr_user}
							socket={socket}
						/>
					))}
				</div>
			</div>

			<div className='chat-bottom height-fit-content max-height-half'>
				<form ref={form} onSubmit={submitForm} className="chat-form">
					<div className="avatar-wrapper">
						<Avatar
							size={'chat'}
							user={user!}
							noBorder={true}
						/>
					</div>
					<div className="input-wrapper">
						{attachment && (
							<div className="doc-wrapper">
								<div className="doc-background">
									<div className="remove-attachment" onClick={clearAttachment}>
										<Icon name={ICONS.ADD_CLOSE_CIRCLE} size={26} color={COLORS.WHITE} ></Icon>
									</div>
									{
										attachment.thumbnail
										&& attachment.thumbnail_status > 0
										&& <Thumbnail thumbnail={attachment.thumbnail} />
									}
									<div className={classNames('doc-title')}>
										<div className={classNames('doc-type', getDocIcon(attachment))}>
											{attachment.location.split('.').pop()}
										</div>
										<div className="doc-name">
											<label>{getName(attachment.originalName)}</label>
										</div>
										<div className="doc-extension">
											<label>{attachment.location.split('.').pop()}</label>
										</div>
									</div>
								</div>
							</div>

						)}
						<div className="input-container">
							<span
								contentEditable={true}
								ref={input}
								className="chat-input"
								onKeyDown={(e) => chatKeyInput(e, () => {
									sendMessage();
								})}
								onInput={(e) => chatInputChange(e, (innerText: string) => {
									setInputText(innerText);
								})}
								onCompositionStart={() => setComposing(true)}
								onCompositionEnd={() => setComposing(false)}
								placeholder="Start typing your message"
							></span>
							<div className="form-actions">
								<button className="attachment" type={'button'} onClick={selectAttachment}>
									{uploading ? (
										<LoadingSpinner
											height={16}
											width={16}
											color={colors.textLight}
											stroke={10}
										/>
									) : (
										<Icon
											name={ICONS.ATTACHMENT}
											size={24}
											color={attachment ? COLORS.WHITE : COLORS.DEFAULT_GRAY}
										/>
									)}
								</button>
								<button className={classNames('send')}>
									<Icon
										name={ICONS.SEND}
										size={25}
										color={inputText.trim() ? COLORS.WHITE : COLORS.DEFAULT_GRAY}
									/>
								</button>
							</div>
						</div>
					</div>
				</form>
				<input type="file" style={{ display: 'none' }} ref={file} onChange={handleUpload} accept={SUPPORTED_CHAT_FILE_FORMATS.MIME_TYPES.toString()} />
			</div>
		</div>
	);
}

export default connect<StateProps, DispatchProps, OwnProps>(
	mapStateToProps,
	mapDispatchToProps,
)(ChatRoomComponent);
